// Library Imports
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

//Local Imports
import EditItemName from 'modules/Common/Components/EditItemName';
import SmallButton from 'modules/Content/Shared/SmallButton';
import SelectTrigger from '../AutomationTrigger/SelectTrigger';
import SelectEmail from '../SelectEmail/SelectEmail';
import { RootState } from 'store';
import { createWorkflowAPI, eventsAPI, resetTriggerWorkflowLists } from '../actions';
import { EVENT_IDS, routes } from '../constants';
import ConfirmDialog from 'modules/Common/Components/ConfirmDialog';
import SelectList from '../SelectList/SelectList';
import useQuery from 'hooks/useQuery';

type Props = {};

const TriggerWrapper = (props: Props) => {
  // Local state
  const [showTrigger, setShowTrigger] = useState(true);
  const [selectedTrigger, setSelectedTrigger] = useState(-1);
  const [selectedEmail, setSelectedEmail] = useState(-1);
  const [selectedList, setSelectedListId] = useState(-1);
  const [showWorkflowBuildingError, setShowWorkflowBuildingError] = useState(false);

  // Redux states
  const workflowLoading: boolean = useSelector((state: RootState) => state.automation.workflow.loading);
  const createWorkflowError: boolean = useSelector((state: RootState) => state.automation.workflow.error);

  // Local Variables
  const history = useHistory();
  const query = useQuery();
  const fromRedirection = query.get('from') === 'redirection';
  const label = showTrigger ? ' Select an event' : selectedTrigger === 14 ? 'Select a list' : ' Select an email';
  const ButtonText =
    selectedTrigger === EVENT_IDS.SHARE_PROFILE && !workflowLoading
      ? 'Start Building Your Workflow'
      : workflowLoading
      ? 'Building Workflow...'
      : 'Next';

  // Component Logic
  const selectTrigger = (id: number) => {
    if (showWorkflowBuildingError) setShowWorkflowBuildingError(false);
    setSelectedTrigger(id);
  };

  const goNext = () => {
    if (selectedTrigger === 14 || fromRedirection) {
      resetTriggerWorkflowLists();
    }
    setShowTrigger(false);
  };

  const selectEmail = (id: number) => {
    setSelectedEmail(id);
  };

  const goBackMain = () => {
    if (showTrigger) {
      // go back to main page
      history.push(routes.DEFAULT);
    } else {
      setShowTrigger(true);
      setSelectedEmail(-1);
      setSelectedTrigger(-1);
      setSelectedListId(-1);
    }
  };

  useEffect(() => {
    if (fromRedirection) {
      selectTrigger(14);
      triggerSelectButtonHandler();
      // deleting param added for redirection
      query.delete('from');
      history.replace({
        search: query.toString(),
      });
    } else eventsAPI();
    return () => {};
  }, []);

  const triggerSelectButtonHandler = () => {
    if (showWorkflowBuildingError) setShowWorkflowBuildingError(false);
    if (selectedTrigger === EVENT_IDS.SHARE_PROFILE) {
      createWorkflowAPI(0, EVENT_IDS.SHARE_PROFILE, 0, history);
    } else {
      goNext();
    }
  };

  const confirmDialogHandler = () => {
    setShowWorkflowBuildingError(false);
  };

  const cancelDialogHandler = () => {
    setShowWorkflowBuildingError(false);
  };

  useEffect(() => {
    if (createWorkflowError) {
      setShowWorkflowBuildingError(true);
    }
  }, [createWorkflowError]);

  return (
    <>
      <Wrapper>
        {
          <div className="d-flex">
            <EditItemName itemName={label} nonEditable={true} goBackFunction={goBackMain} />
          </div>
        }
        <WhiteBoxWrapper>
          <div className="trigger-sidebar">
            <h4>Event Categories</h4>
            <div className="tabs-item">View All</div>
          </div>
          {showTrigger ? (
            <SelectTrigger selectedTriggerId={selectedTrigger} setSelectedTrigger={selectTrigger} goBackFunction={goBackMain} />
          ) : selectedTrigger === 14 ? (
            <SelectList selectedListId={selectedList} setSelectedList={setSelectedListId} />
          ) : (
            <SelectEmail selectedEmailId={selectedEmail} setSelectedEmail={selectEmail} />
          )}
        </WhiteBoxWrapper>
        <div className="save-button-container">
          {showTrigger ? (
            <SmallButton disabled={selectedTrigger === -1 || workflowLoading} title={ButtonText} onClick={triggerSelectButtonHandler} />
          ) : (
            <SmallButton
              disabled={(selectedEmail === -1 && selectedList === -1) || workflowLoading}
              title={workflowLoading ? 'Building Workflow...' : 'Start Building Your Workflow'}
              onClick={() => createWorkflowAPI(selectedEmail, selectedTrigger, selectedList, history)}
            />
          )}
        </div>
      </Wrapper>
      {showWorkflowBuildingError ? (
        <DialogWrapper>
          <ConfirmDialog
            confirm={'Okay'}
            onConfirm={confirmDialogHandler}
            onCancel={cancelDialogHandler}
            title={'Something went wrong!'}
            titleQuestion={'Please try again.'}
          />
        </DialogWrapper>
      ) : null}
    </>
  );
};

export default TriggerWrapper;

const Wrapper = styled.div`
  width: 100%;
  display: block;
  margin-top: 16px;
  .email-name {
    font-size: 26px !important;
    @media (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 22px !important;
    }
  }
  .save-button-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    @media (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-right: 25px;
    }
  }
`;

const WhiteBoxWrapper = styled.div`
  width: 100%;
  display: flex;
  border-radius: 8px;
  background-color: #ffffff;
  height: calc(100vh - 290px);
  margin-top: 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    height: calc(100vh - 225px);
  }
  /* overflow-y: scroll; */
  scrollbar-width: thin;
  scrollbar-color: #001760 #efefef;
  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
    border-radius: 5px;
  }
  /* Track */
  &::-webkit-scrollbar-track {
    background: #fff;
    margin-top: 0;
    margin-bottom: 5px;
    margin-left: 5px;
  }
  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #001760;
  }
  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  .trigger-sidebar {
    border-right: 1px solid #dddddd;
    width: 265px;
    height: 100%;
    @media all and (max-width: 1439px) {
      width: 194px;
      min-width: 194px;
      /* min-height: unset;
      height: calc(100vh - 225px);
      overflow: auto; */
    }
    /* overflow-y: scroll; */
    scrollbar-width: thin;
    scrollbar-color: #001760 #efefef;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 0;
      margin-bottom: 5px;
      margin-left: 5px;
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
    h4 {
      color: #424b50;
      font-size: 20px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      margin: 22px 24px 10px 24px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 16px;
      }
    }
    .tabs-item {
      padding: 12px 0 8px 27px;
      background-color: #e6f5ea;
      width: 100%;
      color: #424b50;
      font-size: 16px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      position: relative;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 14px;
      }
      &:before {
        content: '';
        width: 12px;
        height: 100%;
        background-color: #1ec252;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }
`;

const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
`;
